import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Button, Checkbox, ListItemText, MenuItem, TextField } from '@mui/material';
import { DesktopDatePicker, DesktopTimePicker } from '@mui/x-date-pickers';
import moment from 'moment';

import {
  getDefaultSelectedFinanceAccessForRole,
  getFinanceAccessOptionsForRole
} from 'components/pages/study/StudyDetails/SiteDetails/AddOrEditPersonnelModal/AddRole/financeAccessUtils';
import { isGroupAssignAvailableForRole } from 'components/pages/study/StudyDetails/SiteDetails/AddOrEditPersonnelModal/AddRole/groupAssignUtils';
import { prepareAddRoleRequest } from 'components/pages/study/StudyDetails/SiteDetails/AddOrEditPersonnelModal/AddRole/requestUtils';
import {
  isFinanceAccessRole,
  ssuRoles
} from 'components/pages/study/StudyDetails/SiteDetails/AddOrEditPersonnelModal/AddRole/roleUtils';
import { financeAccessFieldToName } from 'components/pages/study/StudyDetails/SiteDetails/AddOrEditPersonnelModal/constants';
import { preparePersonnelInfoState } from 'components/pages/study/StudyDetails/SiteDetails/AddOrEditPersonnelModal/utils';

import { UserApiApi } from '../../../../../../../api';
import GroupAssignApi from '../../../../../../../api/protocol/GroupAssignApi';
import NotificationManager from '../../../../../../../common/notifications/NotificationManager';
import { YEAR_DASH_MONTH_DASH_DAY } from '../../../../../../../constants/dateFormat';
import { SOMETHING_WENT_WRONG } from '../../../../../../../constants/notificationMessages';
import { HH_COLON_MM } from '../../../../../../../constants/timeFormat';
import { NS_COMMON, NSPS_SITE } from '../../../../../../../services/i18n/namespaces';
import { getTittleByRole } from '../../../../../../../services/personnelService';

import './AddRole.scss';

const DEFAULT_START_TIME = moment('00:00', HH_COLON_MM);
const DEFAULT_EXPIRE_TIME = moment('23:59', HH_COLON_MM);
const SELECT_ALL_OPTION = '__SELECT_ALL__';

const defaultForm = {
  role: '',
  groupAssign: null,
  financeAccess: [],
  startDate: null,
  startTime: DEFAULT_START_TIME,
  expireDate: null,
  expireTime: DEFAULT_EXPIRE_TIME
};

export default function AddRole({
  ssuId,
  studyId,
  studyName,
  isGroupAssignStudy,
  siteName,
  personnelId,
  setPersonnelInfo,
  setRoles
}) {
  const { t } = useTranslation();

  const [form, setForm] = useState(defaultForm);
  const { role, groupAssign, financeAccess, startDate, startTime, expireDate, expireTime } = form;

  const [roleError, setRoleError] = useState(false);
  const [groupAssignError, setGroupAssignError] = useState(false);
  const [financeAccessOptions, setFinanceAccessOptions] = useState([]);
  const [groupAssignRoleOptions, setGroupAssignRoleOptions] = useState([]);
  const [groupAssigns, setGroupAssigns] = useState([]);

  useEffect(() => {
    GroupAssignApi.getStudyGroupAssigns(ssuId, studyId).then(({ data }) => setGroupAssigns(data));
  }, [ssuId, studyId]);

  function setField(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  const handleRoleChange = useCallback(
    e => {
      const newRole = e.target.value;
      setForm(prev => ({ ...prev, role: newRole, groupAssign: null, financeAccess: [] }));
      setRoleError(false);
      setGroupAssignError(false);
      setGroupAssignRoleOptions(groupAssigns.filter(g => isGroupAssignAvailableForRole(g, newRole)));
      setFinanceAccessOptions(getFinanceAccessOptionsForRole(newRole));
      setField('financeAccess', getDefaultSelectedFinanceAccessForRole(newRole));
    },
    [groupAssigns]
  );

  function handleGroupAssignChange(_, value) {
    setField('groupAssign', value);
    if (value) setGroupAssignError(false);
  }

  const allFinanceSelected =
    financeAccessOptions.length > 0 && financeAccessOptions.every(opt => financeAccess.includes(opt));
  const someFinanceSelected = financeAccess.length > 0 && !allFinanceSelected;
  const handleFinanceAccessChange = useCallback(
    (_, selected) => {
      let newSelection;
      if (selected.includes(SELECT_ALL_OPTION)) {
        newSelection = allFinanceSelected ? [] : financeAccessOptions;
      } else {
        newSelection = selected;
      }
      if (!newSelection.includes('financeDetailsAccess')) {
        newSelection = newSelection.filter(opt => opt !== 'remittanceNotification');
      }
      setField('financeAccess', newSelection);
    },
    [allFinanceSelected, financeAccessOptions]
  );

  const isFinanceOptionDisabled = useCallback(
    option => option === 'remittanceNotification' && !financeAccess.includes('financeDetailsAccess'),
    [financeAccess]
  );

  function handleStartDateChange(value) {
    setForm(prev => ({ ...prev, startDate: value, startTime: value ? prev.startTime : DEFAULT_START_TIME }));
  }

  function handleExpireDateChange(value) {
    setForm(prev => ({ ...prev, expireDate: value, expireTime: value ? prev.expireTime : DEFAULT_EXPIRE_TIME }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    let hasError = false;
    if (!role) {
      setRoleError(true);
      hasError = true;
    }
    if (isGroupAssignStudy && !groupAssign) {
      setGroupAssignError(true);
      hasError = true;
    }
    if (hasError) {
      return;
    }
    UserApiApi.addRole(ssuId, personnelId, prepareAddRoleRequest(form))
      .then(({ data }) => {
        NotificationManager.success(t('addOrEditPersonnel.addRole.addRoleSuccess', { ns: NSPS_SITE }));
        setPersonnelInfo(preparePersonnelInfoState(data.personnelInfo));
        setRoles(data?.roles);
        setForm(defaultForm);
      })
      .catch(err => {
        if (err.response.status === 400) {
          NotificationManager.error('Errors: ' + err.response.data.errors.join('; '));
        } else if (err.response.status === 404) {
          NotificationManager.error(err.response.data.message);
        } else {
          NotificationManager.error(SOMETHING_WENT_WRONG);
        }
      });
  }

  function getFinanceAccessDisplayValue(financeAccessOption) {
    return financeAccessOption === SELECT_ALL_OPTION
      ? t('selectAll', { ns: NS_COMMON })
      : financeAccessFieldToName[financeAccessOption];
  }

  return (
    <>
      <div className="add-or-edit-personnel-modal-section-header">
        <h5 className="add-or-edit-personnel-modal-section-title">
          {t('addOrEditPersonnel.addRole.addRole', { ns: NSPS_SITE })}
        </h5>
      </div>
      <div className="add-or-edit-personnel-modal-section-body">
        <form onSubmit={handleSubmit}>
          <div className="add-role-fields-grid">
            <div className="add-role-fields-row">
              <TextField
                label={t('study', { ns: NS_COMMON })}
                variant="outlined"
                className="add-or-edit-personnel-modal-field"
                value={studyName}
                slotProps={{ input: { disabled: true } }}
              />
              <TextField
                label={t('site', { ns: NS_COMMON })}
                variant="outlined"
                className="add-or-edit-personnel-modal-field"
                value={siteName}
                slotProps={{ input: { disabled: true } }}
              />
            </div>
            <TextField
              label={t('addOrEditPersonnel.role', { ns: NSPS_SITE })}
              variant="outlined"
              select
              className="add-or-edit-personnel-modal-field"
              value={role}
              onChange={handleRoleChange}
              error={roleError}
              helperText={roleError ? 'Role is required' : ''}
            >
              {ssuRoles.map(r => (
                <MenuItem key={r} value={r}>
                  {getTittleByRole(r)}
                </MenuItem>
              ))}
            </TextField>
            <div className="add-role-fields-row">
              <Autocomplete
                className="add-or-edit-personnel-modal-field"
                options={groupAssignRoleOptions}
                value={groupAssign}
                onChange={handleGroupAssignChange}
                getOptionLabel={option => `${option.name} : ${option.type}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={!role || !isGroupAssignStudy}
                clearOnEscape
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('addOrEditPersonnel.group', { ns: NSPS_SITE })}
                    variant="outlined"
                    error={groupAssignError}
                    helperText={groupAssignError ? 'Group is required' : ''}
                  />
                )}
              />
              <Autocomplete
                className="add-or-edit-personnel-modal-field"
                multiple
                disableCloseOnSelect
                options={[SELECT_ALL_OPTION, ...financeAccessOptions]}
                value={financeAccess}
                onChange={handleFinanceAccessChange}
                getOptionLabel={getFinanceAccessDisplayValue}
                isOptionEqualToValue={(option, value) => option === value}
                getOptionDisabled={isFinanceOptionDisabled}
                disabled={!role || !isFinanceAccessRole(role)}
                renderOption={(props, option, { selected }) => {
                  const { key, ...restProps } = props;
                  const isSelectAll = option === SELECT_ALL_OPTION;
                  return (
                    <li key={key} {...restProps}>
                      <Checkbox
                        checked={isSelectAll ? allFinanceSelected : selected}
                        indeterminate={isSelectAll ? someFinanceSelected : false}
                        disabled={isFinanceOptionDisabled(option)}
                        sx={{ mr: 1 }}
                      />
                      <ListItemText primary={getFinanceAccessDisplayValue(option)} />
                    </li>
                  );
                }}
                renderValue={() => null}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('addOrEditPersonnel.financeAccess', { ns: NSPS_SITE })}
                    variant="outlined"
                    slotProps={{
                      ...params.slotProps,
                      input: {
                        ...params.InputProps,
                        inputProps: {
                          ...params.inputProps,
                          value: financeAccess.map(opt => financeAccessFieldToName[opt]).join(', '),
                          readOnly: true,
                          style: {
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap'
                          }
                        }
                      }
                    }}
                  />
                )}
              />
            </div>
            <div className="add-role-fields-row">
              <DesktopDatePicker
                label={t('startDate', { ns: NS_COMMON })}
                value={startDate}
                onChange={handleStartDateChange}
                format={YEAR_DASH_MONTH_DASH_DAY}
                slotProps={{
                  textField: { variant: 'outlined', className: 'add-or-edit-personnel-modal-field' },
                  actionBar: { actions: ['clear'] }
                }}
              />
              <DesktopTimePicker
                label={t('addOrEditPersonnel.addRole.startTime', { ns: NSPS_SITE })}
                timezone="system"
                value={startTime}
                onChange={value => setField('startTime', value)}
                format={HH_COLON_MM}
                disabled={!startDate}
                readOnly={!startDate}
                slotProps={{ textField: { variant: 'outlined', className: 'add-or-edit-personnel-modal-field' } }}
              />
              <DesktopDatePicker
                label={t('addOrEditPersonnel.expireDate', { ns: NSPS_SITE })}
                value={expireDate}
                onChange={handleExpireDateChange}
                format={YEAR_DASH_MONTH_DASH_DAY}
                slotProps={{
                  textField: { variant: 'outlined', className: 'add-or-edit-personnel-modal-field' },
                  actionBar: { actions: ['clear'] }
                }}
              />
              <DesktopTimePicker
                label={t('addOrEditPersonnel.addRole.expireTime', { ns: NSPS_SITE })}
                timezone="system"
                value={expireTime}
                onChange={value => setField('expireTime', value)}
                format={HH_COLON_MM}
                disabled={!expireDate}
                readOnly={!expireDate}
                slotProps={{ textField: { variant: 'outlined', className: 'add-or-edit-personnel-modal-field' } }}
              />
            </div>
            <div className="add-or-edit-personnel-modal-actions">
              <Button type="submit" variant="contained" disableElevation>
                {t('addOrEditPersonnel.addRole.addRole', { ns: NSPS_SITE })}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
